import { useEffect, useRef } from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { colord } from 'colord';
import { useAors } from 'hooks/useAors';
import type { AreaOfResponsibility } from 'types/aor';
import AorPopupContent from '../../AorsTab/AorPopupContent';

const DEFAULT_AOR_RGB = { r: 255, g: 140, b: 0 };

/**
 * Manages the lifecycle of the USCG AOR GeoJSONLayer. Converts raw AOR
 * geometries into a Blob-backed GeoJSONLayer. This should be converted to use a
 * VectorTileLayer once the layer versioning/approval UI is implemented.
 */
export function useUscgAorLayerSync({
  mapView,
  isVisible,
  aorFilterIds,
}: {
  mapView: __esri.MapView | null;
  isVisible: boolean;
  aorFilterIds: string[] | null;
}) {
  const { data: aors = [] } = useAors();

  // Track the AOR layer so we can remove/destroy it between updates.
  const layerRef = useRef<GeoJSONLayer | null>(null);
  // Track the blob URL so we can revoke it and avoid memory leaks when the layer updates/unmounts.
  const urlRef = useRef<string | null>(null);
  // Track the popup React root so we can unmount it and avoid memory leaks.
  const popupRootRef = useRef<Root | null>(null);

  useEffect(() => {
    if (!mapView?.map) {
      return;
    }

    const map = mapView.map;

    // Remove any existing AOR layer + blob URL
    if (layerRef.current) {
      map.remove(layerRef.current);
      layerRef.current.destroy();
      layerRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }

    // Optionally remove AORs from map via the AOR Filter UI
    const filteredAors =
      aorFilterIds && aorFilterIds.length
        ? aors.filter((aor) => aorFilterIds.includes(String(aor.id)))
        : aors;

    // Build a map of AOR ID to AOR for quick parent lookups
    const aorMap = new Map(filteredAors.map((aor) => [aor.id, aor]));

    const getAorColor = (aor: AreaOfResponsibility): string | null => {
      if (aor.color) return aor.color;

      if (!aor.parent_item_id) return null;

      const parent = aorMap.get(aor.parent_item_id);
      if (!parent) return null;

      return getAorColor(parent);
    };

    // Build GeoJSON features from AOR current_location_geom
    const features: Feature[] = [];
    for (const aor of filteredAors) {
      if (!aor.current_location_geom) continue;

      const effectiveColor = getAorColor(aor);
      const parsedColor = effectiveColor ? colord(effectiveColor) : null;
      const { r, g, b } = parsedColor?.isValid()
        ? parsedColor.toRgb()
        : DEFAULT_AOR_RGB;

      try {
        const geometry = JSON.parse(aor.current_location_geom) as Geometry;

        features.push({
          type: 'Feature',
          geometry,
          properties: {
            id: aor.id,
            name: aor.name,
            description: aor.description,
            levelName: aor.level_name,
            color: effectiveColor || '',
            r: r,
            g: g,
            b: b,
          },
        });
      } catch (e) {
        // If parsing fails for a particular AOR, skip it
        console.warn('Failed to parse AOR geometry', aor.id, e);
      }
    }

    if (features.length === 0) {
      return;
    }

    const featureCollection: FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    const blob = new Blob([JSON.stringify(featureCollection)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    urlRef.current = url;

    const layer = new GeoJSONLayer({
      id: 'uscg-aors',
      title: 'AORs',
      url,
      visible: isVisible,
      popupTemplate: {
        title: '{name}',
        content: (feature: {
          graphic: { attributes: { id: string; description: string } };
        }) => {
          // Defer unmounting previous popup root to avoid race condition with React rendering
          if (popupRootRef.current) {
            popupRootRef.current = null;
          }

          const container = document.createElement('div');
          const root = createRoot(container);
          popupRootRef.current = root;

          const aorId = feature.graphic.attributes.id;
          const description = feature.graphic.attributes.description;

          root.render(
            <AorPopupContent aorId={aorId} description={description} />
          );
          return container;
        },
      },
      renderer: {
        type: 'unique-value',
        field: 'id',
        uniqueValueInfos: filteredAors.map((aor) => {
          const effectiveColor = getAorColor(aor);
          const parsedColor = effectiveColor ? colord(effectiveColor) : null;
          const { r, g, b } = parsedColor?.isValid()
            ? parsedColor.toRgb()
            : DEFAULT_AOR_RGB;

          return {
            value: aor.id,
            symbol: {
              type: 'simple-fill',
              color: [r, g, b, 0.3],
              outline: {
                color: [r, g, b, 1],
                width: 2,
              },
            },
          };
        }),
      },
    });

    // Add AOR layer behind other operational layers so uscg point/polygon symbology is drawn on top
    map.add(layer);
    map.reorder(layer, 0);

    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.remove(layerRef.current);
        layerRef.current.destroy();
        layerRef.current = null;
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      if (popupRootRef.current) {
        popupRootRef.current = null;
      }
    };
  }, [mapView, aors, isVisible, aorFilterIds]);
}
